import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import confetti from 'canvas-confetti'
import toast from 'react-hot-toast'
import { capstoneService } from '../../services'
import achievementToast from '../../components/AchievementToast'
import CelebrationModal from '../../components/CelebrationModal'
import CapstoneTaskList from '../../components/capstones/CapstoneTaskList'
import CapstoneHintSystem from '../../components/capstones/CapstoneHintSystem'
import TimeTracker from '../../components/capstones/TimeTracker'
import FileUpload from '../../components/capstones/FileUpload'
import SubmittedFilesDisplay from '../../components/capstones/SubmittedFilesDisplay'
import GitHubSubmission from '../../components/submissions/GitHubSubmission'
import GitHubMetadata from '../../components/submissions/GitHubMetadata'
import StarterCodeSection from '../../components/capstones/StarterCodeSection'
import DiscussionList from '../../components/discussions/DiscussionList'
import { useProgressStore } from '../../stores/progressStore'
import {
  RocketLaunchIcon,
  ClockIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  FolderArrowDownIcon,
  TrophyIcon,
  ListBulletIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid, TrophyIcon as TrophySolid } from '@heroicons/react/24/solid'

export default function CapstoneView() {
  const { id } = useParams()
  const { fetchProgress, getCompletionPercentage } = useProgressStore()
  const [capstone, setCapstone] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [submissionUrl, setSubmissionUrl] = useState('')
  const [submissionNotes, setSubmissionNotes] = useState('')
  const [showSubmitForm, setShowSubmitForm] = useState(false)
  const [showCelebrationModal, setShowCelebrationModal] = useState(false)
  const [earnedBadges, setEarnedBadges] = useState([])
  const [draftSubmissionId, setDraftSubmissionId] = useState(null)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    fetchCapstone()
  }, [id])

  useEffect(() => {
    if (showSubmitForm && !draftSubmissionId) {
      fetchDraftSubmission()
    }
  }, [showSubmitForm])

  const fetchDraftSubmission = async () => {
    try {
      const response = await capstoneService.getDraftSubmission(id)
      setDraftSubmissionId(response.submission.id)
      setUploadedFiles(response.submission.files || [])
    } catch (err) {
      console.error('Failed to fetch draft submission:', err)
    }
  }

  const fetchCapstone = async () => {
    try {
      setLoading(true)
      const data = await capstoneService.getById(id)
      setCapstone(data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load capstone')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setValidationErrors({}) // Clear previous errors
    
    try {
      setSubmitting(true)
      await capstoneService.submit(id, {
        github_repo_url: submissionUrl, // Send as GitHub URL
        notes: submissionNotes,
      })
      
      // Success - show toast instead of alert
      toast.success('Capstone submitted successfully! 🎉')
      setShowSubmitForm(false)
      setSubmissionUrl('')
      setSubmissionNotes('')
      fetchCapstone()
    } catch (err) {
      // Handle validation errors
      if (err.response?.status === 422 && err.response?.data?.errors) {
        // Laravel validation errors
        setValidationErrors(err.response.data.errors)
        toast.error('Please fix the validation errors')
      } else {
        // Other errors
        const errorMessage = err.response?.data?.message || 'Failed to submit capstone'
        toast.error(errorMessage)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleMarkComplete = async () => {
    if (completing || capstone?.user_progress?.status === 'completed') return
    
    try {
      setCompleting(true)
      const response = await capstoneService.updateProgress(id, {
        status: 'completed',
        progress_percentage: 100,
      })
      
      // Show capstone completion toast
      achievementToast.capstoneComplete(capstone.title)
      
      // Trigger confetti - extra celebration for capstone completion!
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 }
      })
      
      // Check for earned badges
      if (response.earned_badges && response.earned_badges.length > 0) {
        setEarnedBadges(response.earned_badges)
        
        // Show individual badge toasts
        response.earned_badges.forEach((badge, index) => {
          setTimeout(() => {
            achievementToast.badge(badge)
          }, (index + 1) * 1500)
        })

        // Show celebration modal after a delay
        setTimeout(() => {
          setShowCelebrationModal(true)
        }, response.earned_badges.length * 1500 + 1000)
      }
      
      await fetchCapstone()
      await fetchProgress()

      // Check for milestone achievements
      const newCompletionPercentage = getCompletionPercentage()
      if ([25, 50, 75, 100].includes(newCompletionPercentage)) {
        setTimeout(() => {
          achievementToast.milestone(newCompletionPercentage)
        }, response.earned_badges?.length ? response.earned_badges.length * 1500 + 2000 : 2000)
      }
    } catch (err) {
      achievementToast.error(err.response?.data?.message || 'Failed to mark capstone as complete')
    } finally {
      setCompleting(false)
    }
  }

  const getDifficultyColor = (capstone) => {
    const hours = capstone.estimated_hours
    if (hours <= 8) return 'text-green-600 bg-green-50 border-green-200'
    if (hours <= 12) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-red-600 bg-red-50 border-red-200'
  }

  const getDifficultyText = (capstone) => {
    const hours = capstone.estimated_hours
    if (hours <= 8) return 'Intermediate'
    if (hours <= 12) return 'Advanced'
    return 'Expert'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading capstone...</p>
        </div>
      </div>
    )
  }

  if (error || !capstone) {
    return (
      <div className="space-y-4">
        <Link to="/capstones" className="inline-flex items-center text-green-600 hover:text-green-700">
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Capstones
        </Link>
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-red-800 font-semibold">Error loading capstone</h3>
              <p className="text-red-700 text-sm mt-1">{error || 'Capstone not found'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isCompleted = capstone.user_progress?.status === 'completed'
  const hasSubmitted = capstone.user_submission && capstone.user_submission.status !== 'draft'

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link to="/capstones" className="inline-flex items-center text-green-600 hover:text-green-700 font-semibold">
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Back to Capstones
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10">
            <TrophySolid className="h-32 w-32" />
          </div>
        </div>
        
        <div className="relative">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center mb-3">
                <RocketLaunchIcon className="h-8 w-8 mr-3" />
                <span className="text-sm font-semibold bg-green-800/50 px-3 py-1 rounded-full">
                  {capstone.code}
                </span>
              </div>
              <h1 className="text-4xl font-bold mb-3">{capstone.title}</h1>
              <p className="text-green-100 text-lg mb-6 max-w-3xl">{capstone.description}</p>
              
              <div className="flex items-center flex-wrap gap-4">
                <span className={`text-sm font-semibold px-3 py-1 rounded-full border bg-white/10 border-white/20 text-white`}>
                  {getDifficultyText(capstone)}
                </span>
                <div className="flex items-center text-green-100">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  {capstone.estimated_hours} hours
                </div>
              </div>
            </div>
            {isCompleted && (
              <div className="flex items-center bg-yellow-400 text-yellow-900 rounded-full px-4 py-2 ml-4">
                <TrophySolid className="h-6 w-6 mr-2" />
                <span className="font-semibold">Completed</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time Tracker */}
      <TimeTracker capstoneId={id} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Task Checklist */}
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <CapstoneTaskList 
              capstoneId={id} 
              onProgressChange={() => {
                fetchCapstone();
                fetchProgress();
              }}
            />
          </div>

          {/* Progressive Hints */}
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <CapstoneHintSystem 
              capstoneId={id}
              onHintUnlock={() => {
                // Optionally refresh data or show celebration
              }}
            />
          </div>

          {/* Project Overview */}
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-green-600" />
              Project Overview
            </h2>
            <div className="prose prose-lg max-w-none 
              prose-pre:overflow-x-auto prose-pre:whitespace-pre prose-pre:rounded-lg
              prose-code:whitespace-pre-wrap prose-code:break-words
              [&_pre]:overflow-x-auto [&_pre]:max-w-full">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {capstone.content || capstone.description}
              </ReactMarkdown>
            </div>
          </div>

          {/* Starter Code Section */}
          <StarterCodeSection capstone={capstone} />

          {/* Submit Project Section */}
          {!hasSubmitted && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {!showSubmitForm ? (
                <div className="text-center py-6">
                  <TrophyIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Ready to submit your capstone?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Share your completed project for review and portfolio inclusion
                  </p>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={() => setShowSubmitForm(true)}
                      className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl flex items-center"
                    >
                      <DocumentTextIcon className="w-5 h-5 mr-2" />
                      Submit Capstone
                    </button>
                    <button
                      onClick={handleMarkComplete}
                      disabled={completing || capstone?.user_progress?.status === 'completed'}
                      className={`px-6 py-3 rounded-lg font-semibold flex items-center transition-all ${
                        capstone?.user_progress?.status === 'completed'
                          ? 'bg-green-100 text-green-700 border-2 border-green-700 cursor-not-allowed'
                          : 'border-2 border-green-600 text-green-600 hover:bg-green-50'
                      } ${completing ? 'opacity-50 cursor-wait' : ''}`}
                    >
                      {completing ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : capstone?.user_progress?.status === 'completed' ? (
                        <>
                          <TrophySolid className="w-5 h-5 mr-2" />
                          Completed
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="w-5 h-5 mr-2" />
                          Mark as Complete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <RocketLaunchIcon className="h-5 w-5 mr-2 text-green-600" />
                    Submit Your Capstone Project
                  </h3>

                  {/* Validation Errors Summary */}
                  {Object.keys(validationErrors).length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">
                            Please correct the following errors:
                          </h3>
                          <div className="mt-2 text-sm text-red-700">
                            <ul className="list-disc pl-5 space-y-1">
                              {Object.entries(validationErrors).map(([field, errors]) => (
                                errors.map((error, index) => (
                                  <li key={`${field}-${index}`}>{error}</li>
                                ))
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <GitHubSubmission
                    value={submissionUrl}
                    onChange={(url) => {
                      setSubmissionUrl(url)
                      // Clear error when user starts typing
                      if (validationErrors.github_repo_url) {
                        setValidationErrors(prev => ({ ...prev, github_repo_url: undefined }))
                      }
                    }}
                    onValidate={(isValid, repoInfo) => {
                      // Optional: Store repo info for later use
                    }}
                    label="GitHub Repository URL *"
                    placeholder="https://github.com/username/capstone-project"
                    helpText="Submit your capstone project code as a GitHub repository"
                    required={true}
                  />
                  {validationErrors.github_repo_url && (
                    <p className="mt-1 text-sm text-red-600">
                      {validationErrors.github_repo_url[0]}
                    </p>
                  )}

                  <div>
                    <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Notes & Highlights
                    </label>
                    <textarea
                      id="notes"
                      value={submissionNotes}
                      onChange={(e) => {
                        setSubmissionNotes(e.target.value)
                        // Clear error when user starts typing
                        if (validationErrors.notes) {
                          setValidationErrors(prev => ({ ...prev, notes: undefined }))
                        }
                      }}
                      rows={6}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-green-500 ${
                        validationErrors.notes 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-gray-200 focus:border-green-500'
                      }`}
                      placeholder="Describe your implementation, challenges faced, unique features, etc..."
                    />
                    {validationErrors.notes && (
                      <p className="mt-1 text-sm text-red-600">
                        {validationErrors.notes[0]}
                      </p>
                    )}
                  </div>

                  {/* File Upload Section */}
                  {draftSubmissionId && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Supporting Files (Optional)
                      </label>
                      <FileUpload
                        submissionId={draftSubmissionId}
                        onFilesChange={setUploadedFiles}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Upload screenshots, documentation, demo videos, or code samples
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    >
                      {submitting ? 'Submitting...' : 'Submit Project'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSubmitForm(false)}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Submitted Project Display */}
          {hasSubmitted && capstone.user_submission && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <CheckCircleSolid className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Submitted Project
                </h3>
              </div>
              
              {/* GitHub Repository */}
              {capstone.user_submission.github_repo_url && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GitHub Repository
                  </label>
                  {(() => {
                    const repoInfo = capstone.user_submission.github_repo_url.match(
                      /github\.com[\/:]([^\/]+)\/([^\/\.]+)/
                    );
                    if (repoInfo) {
                      const [, owner, repo] = repoInfo;
                      const repoUrl = `https://github.com/${owner}/${repo}`;
                      return (
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start gap-3">
                            <svg className="w-8 h-8 text-gray-800 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                            </svg>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="text-lg font-semibold text-gray-900">{owner}/{repo}</h4>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                  </svg>
                                  Submitted
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a
                                  href={repoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                  </svg>
                                  View Code
                                  <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                                <a
                                  href={`${repoUrl}/commits`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Commits
                                </a>
                                <a
                                  href={`${repoUrl}#readme`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  README
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    // Fallback if URL doesn't match expected format
                    return (
                      <a
                        href={capstone.user_submission.github_repo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 font-medium underline break-all"
                      >
                        {capstone.user_submission.github_repo_url}
                      </a>
                    );
                  })()}
                </div>
              )}
              
              {/* Legacy Project URL (for backward compatibility) */}
              {capstone.user_submission.content && !capstone.user_submission.github_repo_url && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Repository / Demo URL
                  </label>
                  <a
                    href={capstone.user_submission.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 font-medium underline break-all"
                  >
                    {capstone.user_submission.content}
                  </a>
                </div>
              )}

              {/* GitHub Metadata */}
              {capstone.user_submission.github_repo_url && capstone.user_submission.github_metadata && (
                <div className="mb-6">
                  <GitHubMetadata metadata={capstone.user_submission.github_metadata} />
                </div>
              )}

              {/* Project Notes */}
              {capstone.user_submission.notes && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes & Highlights
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4 text-gray-700 whitespace-pre-wrap">
                    {capstone.user_submission.notes}
                  </div>
                </div>
              )}

              {/* Submitted Files */}
              {capstone.user_submission.uploaded_files && (
                <SubmittedFilesDisplay files={capstone.user_submission.uploaded_files} />
              )}

              {/* Submission Status */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Submitted on {new Date(capstone.user_submission.submitted_at).toLocaleDateString()}
                </p>
                {capstone.user_submission.status === 'graded' && capstone.user_submission.feedback && (
                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-blue-900 mb-2">Instructor Feedback</p>
                    <p className="text-sm text-blue-800">{capstone.user_submission.feedback}</p>
                    {capstone.user_submission.score !== null && (
                      <p className="text-sm font-semibold text-blue-900 mt-2">
                        Score: {capstone.user_submission.score}/{capstone.user_submission.max_score}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Skills Covered */}
          {capstone.skills_covered && capstone.skills_covered.source_files && capstone.skills_covered.source_files.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <CodeBracketIcon className="h-5 w-5 mr-2 text-green-600" />
                Source Files
              </h3>
              <ul className="space-y-2">
                {capstone.skills_covered.source_files.map((skill, idx) => (
                  <li key={idx} className="flex items-center text-sm">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{skill}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Deliverables */}
          {capstone.deliverables && capstone.deliverables.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <ListBulletIcon className="h-5 w-5 mr-2 text-green-600" />
                Project Deliverables
              </h3>
              <ul className="space-y-2">
                {capstone.deliverables.map((deliverable, idx) => (
                  <li key={idx} className="flex items-start text-sm">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                    <span className="text-gray-700">{deliverable}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Progress */}
          {capstone.progress && (
            <div className="bg-green-50 rounded-xl border border-green-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Progress</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-700 font-medium">Status</span>
                    <span className="text-green-600 font-semibold capitalize">
                      {capstone.progress.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                {capstone.progress.completion_percentage > 0 && (
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-700 font-medium">Completion</span>
                      <span className="text-green-600 font-semibold">
                        {Math.round(capstone.progress.completion_percentage)}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill bg-green-600" 
                        style={{ width: `${capstone.progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Portfolio Tip */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
            <TrophyIcon className="h-8 w-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Portfolio Builder
            </h3>
            <p className="text-sm text-gray-700">
              This capstone project will be a valuable addition to your portfolio. 
              Make sure to document your work thoroughly and include it in your GitHub profile!
            </p>
          </div>
        </div>
      </div>

      {/* Discussions Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <ChatBubbleLeftRightIcon className="h-6 w-6 mr-2 text-indigo-600" />
          Project Discussions
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Collaborate with peers, ask questions, and share your progress on this capstone project.
        </p>
        <DiscussionList 
          discussionableType="Capstone" 
          discussionableId={id} 
        />
      </div>

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={showCelebrationModal}
        onClose={() => setShowCelebrationModal(false)}
        badges={earnedBadges}
        title="Capstone Completed! 🚀"
        message="Amazing work on completing this capstone project!"
        confettiType="cannons"
      />
    </div>
  )
}
